/**
 * View Skill Template - Platform admin view for skill template details
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, AdminPageLayout, Card, CardHeader, CardTitle, CardContent, Badge, Section, Grid, Col } from '../../components';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/api';
import './ViewSkillTemplate.css';

const ViewSkillTemplate = () => {
  const navigate = useNavigate();
  const { id: templateId } = useParams();
  const { showSuccess, showError } = useToast();

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isClubSkill = window.location.pathname.includes('/admin/skills/');
  const backPath = isClubSkill ? '/admin/skills' : '/admin/skill-templates';

  const loadTemplate = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to load as club skill first if on club skill route, otherwise load as template
      let templateData;
      if (isClubSkill) {
        try {
          templateData = await apiService.request(`/api/skills/club-skills/${templateId}`);
        } catch (clubErr) {
          // Fallback to template if club skill not found
          templateData = await apiService.getSkillTemplate(templateId);
        }
      } else {
        templateData = await apiService.getSkillTemplate(templateId);
      }

      setTemplate(templateData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [templateId, isClubSkill]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  const handleActivate = async () => {
    try {
      if (isClubSkill) {
        await apiService.request(`/api/skills/club-skills/${templateId}`, {
          method: 'PATCH',
          body: JSON.stringify({ is_active: true })
        });
      } else {
        await apiService.updateSkillTemplate(templateId, { is_active: true });
      }
      showSuccess(`${isClubSkill ? 'Club skill' : 'Skill template'} activated successfully`);
      loadTemplate();
    } catch (err) {
      showError(`Failed to activate: ${err.message}`);
    }
  };

  const handleDeactivate = async () => {
    try {
      if (isClubSkill) {
        await apiService.request(`/api/skills/club-skills/${templateId}`, {
          method: 'PATCH',
          body: JSON.stringify({ is_active: false })
        });
      } else {
        await apiService.updateSkillTemplate(templateId, { is_active: false });
      }
      showSuccess(`${isClubSkill ? 'Club skill' : 'Skill template'} deactivated successfully`);
      loadTemplate();
    } catch (err) {
      showError(`Failed to deactivate: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <AdminPageLayout title="VIEW SKILL" maxWidth="1400px">
        <div style={{ padding: '4rem', textAlign: 'center', opacity: 0.6 }}>Loading skill details...</div>
      </AdminPageLayout>
    );
  }

  if (error) {
    return (
      <AdminPageLayout title="VIEW SKILL" maxWidth="1400px">
        <Section title="Error">
          <Card level={1}>
            <p style={{ color: 'var(--color-error)', marginBottom: '1.5rem' }}>Failed to load skill: {error}</p>
            <Button onClick={loadTemplate} variant="primary">Try Again</Button>
          </Card>
        </Section>
      </AdminPageLayout>
    );
  }

  if (!template) {
    return (
      <AdminPageLayout title="VIEW SKILL" maxWidth="1400px">
        <Section title="Not Found">
          <Card level={1}>
            <p style={{ opacity: 0.6, marginBottom: '1.5rem' }}>This skill could not be found.</p>
            <Button onClick={() => navigate(backPath)} variant="outline">
              Back to Skills
            </Button>
          </Card>
        </Section>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title="VIEW SKILL"
      subtitle={template.name}
      backPath={backPath}
      maxWidth="1400px"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>

        <Section
          title="Status"
          actions={
            <div style={{ display: 'flex', gap: '1rem' }}>
              <Button
                onClick={() => navigate(isClubSkill ? `/admin/skills/${templateId}/edit` : `/admin/skill-templates/${templateId}/edit`)}
                variant="edit"
              >
                Edit Skill
              </Button>
              {template.is_active ? (
                <Button
                  onClick={handleDeactivate}
                  variant="warning"
                >
                  Deactivate
                </Button>
              ) : (
                <Button
                  onClick={handleActivate}
                  variant="success"
                >
                  Activate
                </Button>
              )}
            </div>
          }
        >
          <Card level={1}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: 'var(--surface-lvl2)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid var(--color-border-light)'
              }}>
                <Badge variant={template.is_active ? 'success' : 'warning'} dot size="lg" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.25rem' }}>{template.name}</div>
                <div style={{ opacity: 0.6, fontSize: '0.95rem' }}>Category: {template.category?.replace('_', ' ') || 'Uncategorized'}</div>
              </div>
              <Badge variant={template.is_active ? 'success' : 'warning'} size="sm">
                {template.is_active ? 'ACTIVE' : 'INACTIVE'}
              </Badge>
            </div>
          </Card>
        </Section>

        <Grid columns={12} gap="2rem">
          <Col span={7}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              <Section title="Details">
                <Card level={1}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Description</div>
                      <p style={{ lineHeight: 1.6, fontSize: '1rem' }}>{template.description || 'No description provided.'}</p>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', padding: '1.25rem', background: 'var(--surface-lvl2)', borderRadius: '12px' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Category</div>
                        <div style={{ fontWeight: 600 }}>{template.category?.replace('_', ' ') || 'Uncategorized'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Difficulty Level</div>
                        <div style={{ fontWeight: 600 }}>{template.difficulty_level || 'N/A'}</div>
                      </div>
                    </div>
                  </div>
                </Card>
              </Section>

              <Section title="Instructions">
                <Card level={1}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>How to Practice</div>
                      <p style={{ lineHeight: 1.6, fontSize: '0.95rem', whiteSpace: 'pre-wrap' }}>{template.instruction_text || 'No instructions provided.'}</p>
                    </div>
                    {template.instruction_link && (
                      <div style={{ padding: '1rem', borderTop: '1px solid var(--color-border-light)' }}>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Resource Link</div>
                        <a href={template.instruction_link} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          View Instructions &rarr;
                        </a>
                      </div>
                    )}
                  </div>
                </Card>
              </Section>
            </div>
          </Col>

          <Col span={5}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              <Section title="Media">
                <Card level={1}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                    {template.image_url ? (
                      <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid var(--color-border-light)' }}>
                        <img src={template.image_url} alt={template.name} style={{ width: '100%', height: 'auto', display: 'block' }} />
                      </div>
                    ) : (
                      <div style={{ padding: '3rem', textAlign: 'center', background: 'var(--surface-lvl2)', borderRadius: '12px', opacity: 0.5 }}>
                        No image available
                      </div>
                    )}

                    {template.video_url && (
                      <a href={template.video_url} target="_blank" rel="noopener noreferrer" style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.75rem',
                        padding: '1rem',
                        background: 'var(--color-primary)',
                        color: 'white',
                        borderRadius: '12px',
                        textDecoration: 'none',
                        fontWeight: 600
                      }}>
                        Watch Video
                      </a>
                    )}
                  </div>
                </Card>
              </Section>

              <Section title="Information">
                <Card level={1}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.75rem', borderBottom: '1px solid var(--color-border-light)' }}>
                      <span style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)' }}>Created</span>
                      <span style={{ fontWeight: 600 }}>{template.created_at ? new Date(template.created_at).toLocaleDateString() : 'Unknown'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)' }}>Last Updated</span>
                      <span style={{ fontWeight: 600 }}>{template.updated_at ? new Date(template.updated_at).toLocaleDateString() : 'N/A'}</span>
                    </div>
                  </div>
                </Card>
              </Section>
            </div>
          </Col>
        </Grid>
      </div>
    </AdminPageLayout>
  );
};

export default ViewSkillTemplate;
